import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Pencil,
  Trash2,
  UserPlus,
  Search,
  Power,
  UserCheck,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { EmployeesService } from "../../../services/employees.service";
import { rbacService } from "../../../services/rbac.service";
import { warehouseService } from "../../../services/warehouse.service";

function ChipButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-[38px] rounded-full bg-[#d86a78] px-6 text-[13px] font-semibold text-white transition hover:bg-[#c95b69] active:scale-[0.99]"
    >
      {children}
    </button>
  );
}

function SelectLike({ value, onChange, placeholder, options = [] }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const normalizedOptions = [
    { value: "", label: placeholder },
    ...options.map((option) => ({
      value: String(option?.value ?? ""),
      label: String(option?.label ?? option?.value ?? ""),
    })),
  ];

  const selected =
    normalizedOptions.find((option) => String(option.value) === String(value)) ||
    normalizedOptions[0];

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative h-[38px]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[34px] w-full items-center justify-between rounded-[8px] border bg-white px-4 text-left text-[11px] outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f7d7db]"
            : "border-[#f2c6cc] hover:border-[#d86a78]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-slate-500">
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={15}
          className={`ml-2 shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[40px] z-[200] max-h-[250px] overflow-y-auto rounded-[12px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {normalizedOptions.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={`${placeholder}-${option.value || "all"}`}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-[9px] px-3 py-2.5 text-left text-[11px] transition ${
                  active
                    ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function StatusPill({ value }) {
  const normalized = String(value || "").toUpperCase();

  const cls =
    normalized === "APPROVED"
      ? "bg-[#eef9ea] text-[#59a647]"
      : normalized === "REJECTED"
        ? "bg-[#fff1f3] text-[#d86a78]"
        : normalized === "PENDING"
          ? "bg-[#fff8e8] text-[#b98917]"
          : "bg-slate-100 text-slate-500";

  const label =
    normalized === "APPROVED"
      ? "Approved"
      : normalized === "REJECTED"
        ? "Rejected"
        : normalized === "PENDING"
          ? "Pending"
          : "Not Submitted";

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function SummaryCard({ title, value, tone = "default" }) {
  const toneClasses =
    tone === "success"
      ? "border-[#d7f0cf] bg-[#f5fff2] text-[#59a647]"
      : tone === "warning"
        ? "border-[#ffe9bf] bg-[#fff9ec] text-[#b98917]"
        : tone === "danger"
          ? "border-[#f8d7dc] bg-[#fff6f7] text-[#d86a78]"
          : "border-[#f2c6cc] bg-white text-[#d86a78]";

  return (
    <div className={`rounded-[16px] border px-4 py-4 ${toneClasses}`}>
      <div className="text-[12px] font-medium uppercase tracking-wide">
        {title}
      </div>
      <div className="mt-2 text-[28px] font-bold leading-none">{value}</div>
    </div>
  );
}

function normalizeWarehouseRecord(warehouse = {}) {
  const id = String(warehouse?.id || warehouse?._id || "").trim();
  const name = String(
    warehouse?.name || warehouse?.warehouse_name || warehouse?.warehouseName || ""
  ).trim();
  const code = String(warehouse?.code || warehouse?.warehouse_code || "").trim();
  const city = String(warehouse?.city || warehouse?.address?.city || "").trim();
  const state = String(warehouse?.state || warehouse?.address?.state || "").trim();

  return { id, name, code, city, state };
}

function buildWarehouseLookup(warehouses = []) {
  return (Array.isArray(warehouses) ? warehouses : []).reduce((lookup, item) => {
    const normalized = normalizeWarehouseRecord(item);
    if (normalized.id) lookup[normalized.id] = normalized;
    return lookup;
  }, {});
}

function resolveEmployeeWarehouse(employee, warehouseLookup = {}) {
  const raw = employee?._raw || {};
  const assignedWarehouseId = String(
    employee?.assignedWarehouseId ||
      raw?.assigned_warehouse_id ||
      raw?.assignedWarehouseId ||
      ""
  ).trim();
  const directWarehouseName = String(
    employee?.assignedWarehouseName ||
      raw?.assigned_warehouse_name ||
      raw?.assignedWarehouseName ||
      raw?.warehouse_name ||
      raw?.warehouseName ||
      ""
  ).trim();
  const type = String(
    employee?.userType || employee?.role || employee?._source || ""
  ).toLowerCase();
  const isWarehouseEmployee = type.includes("warehouse_employee");
  const allowedIds = Array.isArray(employee?.allowedWarehouseIds)
    ? employee.allowedWarehouseIds.map(String).filter(Boolean)
    : [];

  const formatWarehouse = (warehouse, fallbackId = "") => {
    if (!warehouse) {
      if (directWarehouseName) return directWarehouseName;
      return fallbackId ? `Warehouse ID: ${fallbackId}` : "";
    }

    const name = warehouse.name || directWarehouseName || "Warehouse";
    const code = warehouse.code ? ` (${warehouse.code})` : "";
    return `${name}${code}`;
  };

  if (assignedWarehouseId || directWarehouseName) {
    const warehouse = warehouseLookup[assignedWarehouseId];
    const label = formatWarehouse(warehouse, assignedWarehouseId);
    const location = [warehouse?.city, warehouse?.state].filter(Boolean).join(", ");

    return {
      label: label || assignedWarehouseId || directWarehouseName,
      title: [label, location, assignedWarehouseId ? `ID: ${assignedWarehouseId}` : ""]
        .filter(Boolean)
        .join(" • "),
      missing: false,
    };
  }

  if (allowedIds.length > 0) {
    const labels = allowedIds.map((id) => {
      const warehouse = warehouseLookup[id];
      return formatWarehouse(warehouse, id) || id;
    });

    return {
      label:
        labels.length <= 2
          ? labels.join(", ")
          : `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`,
      title: labels.join(", "),
      missing: false,
    };
  }

  if (isWarehouseEmployee) {
    return {
      label: "Not assigned",
      title: "This warehouse employee has no assigned warehouse.",
      missing: true,
    };
  }

  return { label: "-", title: "No warehouse access assigned", missing: false };
}


function ApprovalModal({
  open,
  row,
  note,
  setNote,
  saving,
  onClose,
  onApprove,
  onReject,
}) {
  if (!open || !row) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-[500px] rounded-[24px] bg-white p-6 shadow-2xl sm:p-7">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fff8e8] text-[#b98917]">
            <UserCheck size={24} />
          </div>

          <div>
            <h3 className="text-[22px] font-bold text-slate-700">
              Review Employee Approval
            </h3>
            <p className="text-[13px] text-slate-500">
              {row.name} ({row.email})
            </p>
            <p className="mt-1 text-[12px] font-medium text-[#b98917]">
              Current status: {row.approvalLabel || row.approvalStatus || "Pending"}
            </p>
          </div>
        </div>

        <div className="mt-5">
          <label className="mb-2 block text-[12px] font-semibold tracking-wide text-slate-500">
            NOTE
          </label>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Add note... (required only for reject)"
            className="w-full rounded-[14px] border border-[#f2c6cc] px-4 py-3 text-[13px] text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
          />
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-[44px] flex-1 rounded-[10px] border border-[#f2c6cc] text-[13px] font-semibold text-slate-500 hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onReject}
            disabled={saving}
            className="h-[44px] flex-1 rounded-[10px] bg-[#d86a78] text-[13px] font-semibold text-white hover:bg-[#c95b69]"
          >
            {saving ? "PLEASE WAIT..." : "Reject"}
          </button>

          <button
            type="button"
            onClick={onApprove}
            disabled={saving}
            className="h-[44px] flex-1 rounded-[10px] bg-[#59a647] text-[13px] font-semibold text-white hover:bg-[#4f963f]"
          >
            {saving ? "PLEASE WAIT..." : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionIconButton({
  children,
  title,
  onClick,
  className = "",
  showDot = false,
}) {
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        className={`transition ${className}`}
        title={title}
        onClick={onClick}
      >
        {children}
      </button>

      {showDot ? (
        <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#ef4444] ring-2 ring-white" />
      ) : null}
    </div>
  );
}

function Actions({
  row,
  onToggleActive,
  onDelete,
  onEdit,
  onAssign,
  onApprove,
  onReject,
}) {
  const approvalStatus = String(row?.approvalStatus || "").toUpperCase();
  const isActive = String(row?.status || "").toLowerCase() === "active";

  const isOnboarded =
    row.onboarding === true ||
    row.onboarding === "Yes";

  const isPending =
    (row.approvalStatus || "").toUpperCase() === "PENDING";

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 text-slate-600">
      <button
        type="button"
        className="transition hover:text-[#d86a78]"
        title="Edit"
        onClick={onEdit}
      >
        <Pencil size={18} />
      </button>

      <ActionIconButton
        title="Review Approval"
        className="hover:text-[#b98917]"
        onClick={onApprove}
        showDot={isPending && isOnboarded}
      >
        <UserCheck
          size={18}
          className={approvalStatus === "PENDING" ? "text-[#b98917]" : "text-slate-500"}
        />
      </ActionIconButton>

      <button
        type="button"
        className={`transition ${isActive
            ? "text-[#69a65f] hover:text-[#4d8a44]"
            : "text-slate-500 hover:text-[#d86a78]"
          }`}
        title={isActive ? "Deactivate Employee" : "Activate Employee"}
        onClick={onToggleActive}
      >
        <Power size={18} />
      </button>

      <button
        type="button"
        className="transition hover:text-[#d86a78]"
        title="Delete"
        onClick={onDelete}
      >
        <Trash2 size={16} className="text-red-500 cursor-pointer" />
      </button>
    </div>
  );
}

export default function EmployeeDetails() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getPositiveNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingAction, setSavingAction] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [warehouseLookup, setWarehouseLookup] = useState({});
  const [approvalSummary, setApprovalSummary] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0,
  });

  const [popup, setPopup] = useState({
    open: false,
    kind: null,
    row: null,
  });

  const [approvalModal, setApprovalModal] = useState({
    open: false,
    row: null,
  });

  const [approvalNote, setApprovalNote] = useState("");
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [role, setRole] = useState(() => searchParams.get("role") || "");
  const [dept, setDept] = useState(() => searchParams.get("dept") || "");
  const [loc, setLoc] = useState(() => searchParams.get("loc") || "");
  const [approvalFilter, setApprovalFilter] = useState(
    () => searchParams.get("approval") || ""
  );
  const [page, setPage] = useState(() =>
    getPositiveNumber(searchParams.get("page"), 1)
  );
  const [pageSize, setPageSize] = useState(() =>
    getPositiveNumber(searchParams.get("limit"), 10)
  );

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError("");

      const [normalized, summary, warehouses] = await Promise.all([
        EmployeesService.listAllForDashboard(),
        EmployeesService.getApprovalSummary(),
        warehouseService.listAllWarehouses().catch(() => []),
      ]);

      setEmployees(Array.isArray(normalized) ? normalized : []);
      setWarehouseLookup(buildWarehouseLookup(warehouses));

      setApprovalSummary({
        pending: Number(summary?.pending || 0),
        approved: Number(summary?.approved || 0),
        rejected: Number(summary?.rejected || 0),
        total: Number(summary?.total || 0),
      });
    } catch (e) {
      setError(e?.message || "Failed to load employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set("page", String(page));
    if (pageSize !== 10) params.set("limit", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (role) params.set("role", role);
    if (dept) params.set("dept", dept);
    if (loc) params.set("loc", loc);
    if (approvalFilter) params.set("approval", approvalFilter);

    setSearchParams(params, { replace: true });
  }, [page, pageSize, q, role, dept, loc, approvalFilter, setSearchParams]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const roles = await rbacService.listRoles();

        if (!mounted) return;

        setRoleOptions(
          (Array.isArray(roles) ? roles : [])
            .map((r) => ({
              value: r?.name || r?.id,
              label: r?.name || r?.id,
            }))
            .filter((x) => x.value)
        );
      } catch {
        if (mounted) setRoleOptions([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const employeesWithWarehouse = useMemo(
    () =>
      employees.map((employee) => ({
        ...employee,
        warehouseDisplay: resolveEmployeeWarehouse(employee, warehouseLookup),
      })),
    [employees, warehouseLookup]
  );

  const deptOptions = useMemo(() => {
    return Array.from(
      new Set(
        employees
          .map((e) => e.department)
          .filter(Boolean)
          .filter((v) => v !== "-")
      )
    ).map((v) => ({ value: v, label: v }));
  }, [employees]);

  const locOptions = useMemo(() => {
    return Array.from(
      new Set(
        employees
          .map((e) => e.location)
          .filter(Boolean)
          .filter((v) => v !== "-")
      )
    ).map((v) => ({ value: v, label: v }));
  }, [employees]);

  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();

    const filtered = employeesWithWarehouse.filter((r) => {
      const matchQ = qq
        ? [
          r.name,
          r.email,
          r.phone,
          r.role,
          r.department,
          r.location,
          r.warehouseDisplay?.label,
          r.approvalLabel,
        ]
          .join(" ")
          .toLowerCase()
          .includes(qq)
        : true;

      const matchRole = role ? String(r.role) === String(role) : true;
      const matchDept = dept ? String(r.department) === String(dept) : true;
      const matchLoc = loc ? String(r.location) === String(loc) : true;
      const matchApproval = approvalFilter
        ? String(r.approvalStatus) === String(approvalFilter)
        : true;

      return matchQ && matchRole && matchDept && matchLoc && matchApproval;
    });

    const total = filtered.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, pageCount);
    const start = (currentPage - 1) * pageSize;

    return {
      total,
      pageCount,
      currentPage,
      items: filtered.slice(start, start + pageSize),
    };
  }, [employeesWithWarehouse, q, role, dept, loc, approvalFilter, page, pageSize]);

  useEffect(() => {
    if (!loading && page > rows.pageCount) setPage(rows.pageCount);
  }, [loading, page, rows.pageCount]);

  useEffect(() => {
    if (loading || rows.total <= 0) return;

    const nextPageSize =
      rows.total < 10
        ? rows.total
        : pageSize > rows.total
          ? rows.total
          : pageSize < 10
            ? 10
            : pageSize;

    if (nextPageSize !== pageSize) {
      setPage(1);
      setPageSize(nextPageSize);
    }
  }, [loading, rows.total, pageSize]);

  const pageSizeOptions = useMemo(() => {
    if (rows.total <= 0) return [10];

    const base = [10, 25, 50, 100, 250, 500];
    const opts = base.filter((n) => n <= rows.total);

    if (!opts.includes(rows.total)) opts.push(rows.total);

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [rows.total]);

  const onChangePageSize = (value) => {
    const n = Number(value);
    setPage(1);
    setPageSize(Number.isFinite(n) && n > 0 ? n : 10);
  };

  const canPrev = rows.currentPage > 1;
  const canNext = rows.currentPage < rows.pageCount;
  const startIndex = rows.total === 0 ? 0 : (rows.currentPage - 1) * pageSize + 1;
  const endIndex =
    rows.total === 0 ? 0 : Math.min(rows.currentPage * pageSize, rows.total);

  const closePopup = () =>
    setPopup({
      open: false,
      kind: null,
      row: null,
    });

  const openTogglePopup = (row) =>
    setPopup({
      open: true,
      kind: row.status === "Active" ? "deactivate" : "activate",
      row,
    });

  const openDeletePopup = (row) =>
    setPopup({
      open: true,
      kind: "delete",
      row,
    });

  const closeApproval = () => {
    setApprovalModal({
      open: false,
      row: null,
    });
    setApprovalNote("");
  };

  const openApproval = (row) => {
    setApprovalModal({
      open: true,
      row,
    });

    setApprovalNote(row.approvalNote || "");
  };

  const onConfirmPopup = async () => {
    if (!popup.row || savingAction) return;

    try {
      setSavingAction(true);

      if (popup.kind === "activate") {
        await EmployeesService.activateEmployee(popup.row.id);
      }

      if (popup.kind === "deactivate") {
        await EmployeesService.deactivateEmployee(popup.row.id);
      }

      if (popup.kind === "delete") {
        await EmployeesService.deleteAdminEmployee(popup.row.id);
      }

      closePopup();
      await loadEmployees();
    } catch (e) {
      alert(e?.message || "Action failed");
    } finally {
      setSavingAction(false);
    }
  };

  const submitApprove = async () => {
    if (!approvalModal.row || savingAction) return;

    try {
      setSavingAction(true);

      await EmployeesService.approveEmployee(approvalModal.row.id, {
        status: "APPROVED",
        note: approvalNote.trim() || undefined,
      });

      closeApproval();
      await loadEmployees();
    } catch (e) {
      alert(e?.message || "Failed to approve employee");
    } finally {
      setSavingAction(false);
    }
  };

  const submitReject = async () => {
    if (!approvalModal.row || savingAction) return;

    if (!approvalNote.trim()) {
      return alert("Rejection note is required");
    }

    try {
      setSavingAction(true);

      await EmployeesService.approveEmployee(approvalModal.row.id, {
        status: "REJECTED",
        note: approvalNote.trim(),
      });

      closeApproval();
      await loadEmployees();
    } catch (e) {
      alert(e?.message || "Failed to reject employee");
    } finally {
      setSavingAction(false);
    }
  };

  return (
    <div className="w-full">
      <ConfirmModal
        open={
          popup.open &&
          (popup.kind === "activate" || popup.kind === "deactivate")
        }
        icon="ban"
        variant="confirm"
        title={
          popup.kind === "activate"
            ? `Are you sure you want to Activate ${popup.row?.name || ""}?`
            : `Are you sure you want to Deactivate ${popup.row?.name || ""}?`
        }
        description={
          popup.kind === "activate"
            ? "This employee will be able to sign in again and use the admin panel."
            : "This employee will not be able to sign in until activated again."
        }
        onClose={closePopup}
        onConfirm={onConfirmPopup}
        cancelText="NO"
        confirmText={savingAction ? "PLEASE WAIT..." : "YES"}
      />

      <ConfirmModal
        open={popup.open && popup.kind === "delete"}
        icon="trash"
        variant="confirm"
        title={`Are you sure you want to Delete ${popup.row?.name || ""}?`}
        description="This will permanently remove the employee record from the list."
        onClose={closePopup}
        onConfirm={onConfirmPopup}
        cancelText="NO"
        confirmText={savingAction ? "DELETING..." : "YES"}
      />

      <ApprovalModal
        open={approvalModal.open}
        row={approvalModal.row}
        note={approvalNote}
        setNote={setApprovalNote}
        saving={savingAction}
        onClose={closeApproval}
        onApprove={submitApprove}
        onReject={submitReject}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
          EMPLOYEE DETAILS
        </h1>

        <div className="flex flex-wrap items-center gap-3">
          <ChipButton onClick={() => navigate("/admin/dashboard/roles")}>
            Manage Roles
          </ChipButton>

          <ChipButton onClick={() => navigate("/admin/dashboard/employees/new")}>
            Add New Employee
          </ChipButton>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard title="Total Employees" value={employees.length} />
        <SummaryCard
          title="Pending Approvals"
          value={approvalSummary.pending}
          tone="warning"
        />
        <SummaryCard
          title="Approved"
          value={approvalSummary.approved}
          tone="success"
        />
        <SummaryCard
          title="Rejected"
          value={approvalSummary.rejected}
          tone="danger"
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-[190px]">
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search employee"
              className="h-[34px] w-full rounded-[8px] border border-[#f2c6cc] bg-white pl-9 pr-4 text-[11px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
            />
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:w-[560px] sm:grid-cols-4">
            <SelectLike
              value={role}
              onChange={(v) => {
                setPage(1);
                setRole(v);
              }}
              placeholder="Role"
              options={roleOptions}
            />

            <SelectLike
              value={dept}
              onChange={(v) => {
                setPage(1);
                setDept(v);
              }}
              placeholder="Department"
              options={deptOptions}
            />

            <SelectLike
              value={loc}
              onChange={(v) => {
                setPage(1);
                setLoc(v);
              }}
              placeholder="Location"
              options={locOptions}
            />

            <SelectLike
              value={approvalFilter}
              onChange={(v) => {
                setPage(1);
                setApprovalFilter(v);
              }}
              placeholder="Approval"
              options={[
                { value: "PENDING", label: "Pending" },
                { value: "APPROVED", label: "Approved" },
                { value: "REJECTED", label: "Rejected" },
                { value: "NOT_SUBMITTED", label: "Not Submitted" },
              ]}
            />
          </div>
        </div>

        <div className="lg:ml-auto">
          <div className="inline-flex h-[30px] items-center rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[14px] font-semibold text-[#d86a78]">
            {rows.total} Visible Employees
          </div>
        </div>
      </div>

      <div className="mt-6 hidden overflow-hidden rounded-[14px] border border-slate-200/70 bg-white md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1320px] border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-[40px] border-b border-slate-200/70 px-4 py-3" />
                {[
                  "Status",
                  "Approval",
                  "Name",
                  "Email",
                  "Phone",
                  "Role",
                  "Department",
                  "Warehouse",
                  "Onboarding",
                  "Location",
                ].map((h) => (
                  <th
                    key={h}
                    className="border-b border-slate-200/70 px-4 py-3 text-left text-[14px] font-bold text-[#d86a78]"
                  >
                    {h}
                  </th>
                ))}
                <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[14px] font-bold text-[#d86a78]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.items.map((r) => {
                const isActive = r.status === "Active";

                return (
                  <tr key={r.id} className="hover:bg-slate-50/50">
                    <td className="border-b border-slate-200/70 px-4 py-4">
                      {/* <input
                        type="checkbox"
                        className="h-[14px] w-[14px] rounded border-slate-300 text-[#d86a78]"
                      /> */}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px]">
                      <span
                        className={
                          isActive ? "text-[#7bbf6a]" : "text-[#e57a86]"
                        }
                      >
                        {r.status}
                      </span>
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      <StatusPill value={r.approvalStatus} />
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-500">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/admin/dashboard/employees/${r.id}`)
                        }
                        className="text-left text-slate-500 hover:text-[#d86a78]"
                        title="Open profile"
                      >
                        {r.name}
                      </button>
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      {r.email}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      {r.phone}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      {r.role}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      {r.department}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      <div
                        className={`max-w-[190px] truncate ${
                          r.warehouseDisplay?.missing ? "font-semibold text-red-500" : ""
                        }`}
                        title={r.warehouseDisplay?.title || r.warehouseDisplay?.label}
                      >
                        {r.warehouseDisplay?.label || "-"}
                      </div>
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      {r.onboarding}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4 text-[13px] text-slate-400">
                      {r.location}
                    </td>

                    <td className="border-b border-slate-200/70 px-4 py-4">
                      <Actions
                        row={r}
                        onEdit={() =>
                          navigate(`/admin/dashboard/employees/${r.id}/edit`)
                        }
                        onAssign={() => alert(`Assign role to ${r.name}`)}
                        onToggleActive={() => openTogglePopup(r)}
                        onDelete={() => openDeletePopup(r)}
                        onApprove={() => openApproval(r)}
                        onReject={() => openApproval(r)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>

      <div className="mt-5 flex flex-col gap-3 rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-center sm:text-left">
          Showing {startIndex}-{endIndex} of {rows.total}
        </div>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center justify-between gap-3 sm:justify-start">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onChangePageSize(e.target.value)}
              className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78]"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-start">
            <button
              type="button"
              onClick={() => canPrev && setPage((p) => p - 1)}
              disabled={!canPrev}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Prev
            </button>

            <span className="min-w-[86px] text-center">
              Page {rows.currentPage} / {rows.pageCount}
            </span>

            <button
              type="button"
              onClick={() => canNext && setPage((p) => p + 1)}
              disabled={!canNext}
              className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mt-4 text-[12px] text-red-500">{error}</div>
      ) : null}

      {loading ? (
        <div className="mt-4 text-[12px] text-slate-400">Loading...</div>
      ) : null}
    </div>
  );
}